import styled from 'styled-components';

// ---------------------------------------------------------------------------
// RH · Recrutamento — estilos ESPECÍFICOS da página de onboarding (Fase 1):
// stepper do pipeline, banner de desqualificação e linhas do checklist. Os
// primitivos de detalhe compartilhados (Card, Fact, Header, BackRow…) vivem em
// ../detailStyles e são re-exportados aqui (mesmo arranjo do CandidateShow).
// ---------------------------------------------------------------------------

export {
   HeaderBar,
   HeaderMain,
   Avatar,
   TitleRow,
   MetaLine,
   Card,
   CardHead,
   FactGrid,
   Fact,
   InlineHint,
   BackRow,
} from '../detailStyles';

const border = ({ theme }) => theme?.colors?.border ?? 'rgba(0,0,0,0.10)';
const muted = ({ theme }) => theme?.colors?.textMuted ?? '#6b7280';
const ink = ({ theme }) => theme?.colors?.textPrimary ?? '#1f1f18';

// ─── Stepper do pipeline (mock .stepper/.step, cand.css) ────────────────────
// Dots de 32px com linhas conectoras e caption abaixo; done = verde-suave com
// ✓, current = azul primário com anel de foco, upcoming = neutro.
export const Stepper = styled.div`
   display: flex;
   align-items: flex-start;
   gap: 0;
   padding: 6px 4px 4px;
`;

export const Step = styled.div`
   flex: 1 1 0;
   display: flex;
   flex-direction: column;
   align-items: center;
   position: relative;
   min-width: 0;

   .track {
      position: relative;
      width: 100%;
      display: flex;
      justify-content: center;
   }
   /* linhas conectoras (metade esquerda/direita de cada passo) */
   .track::before,
   .track::after {
      content: '';
      position: absolute;
      top: 15px;
      height: 2px;
      background: ${border};
      z-index: 0;
   }
   .track::before {
      left: 0;
      right: 50%;
      margin-right: 16px;
   }
   .track::after {
      left: 50%;
      right: 0;
      margin-left: 16px;
   }
   &:first-child .track::before {
      display: none;
   }
   &:last-child .track::after {
      display: none;
   }
   ${({ $state }) =>
      $state === 'done'
         ? '.track::before, .track::after { background: rgba(46,125,50,0.45); }'
         : ''}
   ${({ $state }) =>
      $state === 'current'
         ? '.track::before { background: rgba(46,125,50,0.45); }'
         : ''}

   .dot {
      position: relative;
      z-index: 1;
      width: 32px;
      height: 32px;
      border-radius: 999px;
      display: grid;
      place-items: center;
      font-size: 13px;
      font-weight: 800;
      background: ${({ $state }) =>
         $state === 'done'
            ? 'rgba(46,125,50,0.12)'
            : $state === 'current'
              ? '#4098ef'
              : '#fff'};
      border: 2px solid
         ${({ $state }) =>
            $state === 'done'
               ? 'rgba(46,125,50,0.5)'
               : $state === 'current'
                 ? '#4098ef'
                 : 'rgba(0,0,0,0.12)'};
      color: ${({ $state }) =>
         $state === 'done'
            ? '#1b5e20'
            : $state === 'current'
              ? '#fff'
              : '#9aa0a6'};
      ${({ $state }) =>
         $state === 'current'
            ? 'box-shadow: 0 0 0 3px rgba(64,152,239,0.25);'
            : ''}
   }

   .caption {
      margin-top: 8px;
      font-size: 11.5px;
      line-height: 1.25;
      text-align: center;
      font-weight: 600;
      max-width: 118px;
      color: ${({ $state }) => ($state === 'upcoming' ? muted : ink)};
   }
`;

// Faixa da etapa atual (mock .step-banner): mensagem+hint à esquerda, ação à
// direita, divisor tracejado acima.
export const StepBanner = styled.div`
   display: flex;
   align-items: center;
   justify-content: space-between;
   gap: 16px;
   margin-top: 16px;
   padding-top: 14px;
   border-top: 1px dashed ${border};
   flex-wrap: wrap;

   .msg {
      font-size: 13.5px;
      color: ${muted};
      max-width: 560px;
   }
   .msg b {
      color: ${ink};
      font-weight: 700;
   }
   .hint {
      display: block;
      font-size: 12px;
      color: ${muted};
      margin-top: 3px;
   }
`;

// Painel de trabalho da etapa ativa (passos manuais) — entre o stepper e o
// banner, com o mesmo divisor tracejado.
export const StepPanel = styled.div`
   margin-top: 16px;
   padding-top: 14px;
   border-top: 1px dashed ${border};
`;

// ─── Layout main + rail (mock .work-grid/.rail) ─────────────────────────────
export const WorkGrid = styled.div`
   display: grid;
   grid-template-columns: 1fr 336px;
   gap: 16px;
   align-items: start;

   @media (max-width: 1040px) {
      grid-template-columns: 1fr;
   }
`;

export const Rail = styled.aside`
   display: flex;
   flex-direction: column;
   gap: 16px;
   position: sticky;
   top: 20px;
`;

// Linha k/v do card "Resumo da admissão" (mock .fact).
export const FactRow = styled.div`
   display: flex;
   justify-content: space-between;
   align-items: baseline;
   gap: 12px;
   padding: 9px 0;
   border-bottom: 1px solid ${border};

   &:last-child {
      border-bottom: none;
   }

   .k {
      font-size: 12.5px;
      color: ${muted};
   }
   .v {
      font-size: 13px;
      font-weight: 600;
      color: ${ink};
      text-align: right;
   }
   .v.empty {
      color: #b8b8b8;
      font-weight: 500;
   }
`;

// Linha do card "Progresso da admissão" (mock .prog-item). $state também
// aceita 'bad' (sub-análise reprovada). $sub = sub-item indentado (as duas
// análises do Background Check).
const progDotColor = {
   ok: '#2e7d32',
   now: '#4098ef',
   bad: '#b71c1c',
   wait: '#d3d6da',
};

export const ProgItem = styled.div`
   display: flex;
   align-items: center;
   gap: 10px;
   padding: ${({ $sub }) => ($sub ? '3px 0 3px 20px' : '8px 0')};

   .dot2 {
      width: ${({ $sub }) => ($sub ? '7px' : '10px')};
      height: ${({ $sub }) => ($sub ? '7px' : '10px')};
      border-radius: 999px;
      flex: 0 0 auto;
      background: ${({ $state }) => progDotColor[$state] ?? progDotColor.wait};
      ${({ $state }) =>
         $state === 'now'
            ? 'box-shadow: 0 0 0 3px rgba(64,152,239,0.18);'
            : ''}
   }
   .lbl {
      font-size: ${({ $sub }) => ($sub ? '12px' : '13px')};
      color: ${({ $sub }) => ($sub ? muted : ink)};
   }
   .st {
      margin-left: auto;
      font-size: 11.5px;
      color: ${muted};
      display: inline-flex;
      align-items: center;
      gap: 6px;
   }
`;

export const InfoNote = styled.div`
   display: flex;
   gap: 10px;
   padding: 12px 14px;
   background: rgba(64, 152, 239, 0.06);
   border: 1px solid rgba(64, 152, 239, 0.2);
   border-radius: 10px;

   svg {
      flex: 0 0 auto;
      color: #4098ef;
      margin-top: 1px;
   }
   p {
      margin: 0;
      font-size: 12.5px;
      color: #33506e;
      line-height: 1.45;
   }
`;

// Barra de progresso do checklist (mock .doc-progress).
export const DocProgress = styled.div`
   height: 6px;
   border-radius: 999px;
   background: #eef0f2;
   overflow: hidden;
   margin-bottom: 16px;

   > i {
      display: block;
      height: 100%;
      background: #4098ef;
      border-radius: 999px;
   }
`;

// Sub-contagem no título de section (mock .section-sub).
export const SectionSub = styled.span`
   font-size: 12px;
   color: ${muted};
   font-weight: 500;
   margin-left: 8px;
`;

// Banner terminal (DESQUALIFICADO) — substitui a trilha.
export const DisqualifiedBanner = styled.div`
   border: 1px solid rgba(171, 32, 32, 0.35);
   background: rgba(171, 32, 32, 0.06);
   border-radius: 12px;
   padding: 14px;
   color: #8a1f1f;

   .banner-title {
      font-weight: 800;
      font-size: 0.95rem;
   }
   .banner-reason {
      margin-top: 6px;
      font-size: 0.86rem;
      white-space: pre-wrap;
      color: ${ink};
   }
`;

// ─── Checklist de documentos ─────────────────────────────────────────────────
export const DocGroup = styled.div`
   & + & {
      margin-top: 16px;
   }

   .group-title {
      font-size: 11px;
      font-weight: 700;
      text-transform: uppercase;
      letter-spacing: 0.03em;
      color: ${muted};
      margin-bottom: 8px;
   }
`;

export const DocList = styled.div`
   display: flex;
   flex-direction: column;
   gap: 8px;
`;

export const DocRow = styled.div`
   border: 1px solid ${border};
   border-radius: 12px;
   padding: 10px 14px;
   opacity: ${({ $inactive }) => ($inactive ? 0.6 : 1)};
`;

export const DocRowMain = styled.div`
   display: flex;
   align-items: center;
   justify-content: space-between;
   gap: 12px;
   flex-wrap: wrap;

   .doc-name {
      font-weight: 700;
      font-size: 0.9rem;
      color: ${ink};
      display: flex;
      align-items: center;
      gap: 8px;
      flex-wrap: wrap;
   }
   .doc-file {
      font-size: 0.78rem;
      color: ${muted};
   }
   .doc-actions {
      display: flex;
      align-items: center;
      gap: 8px;
      flex-wrap: wrap;
   }
`;

// Sub-linha expandida (valor VALUE / observações).
export const DocRowDetail = styled.div`
   margin-top: 10px;
   padding-top: 10px;
   border-top: 1px dashed ${border};
   display: flex;
   align-items: flex-end;
   gap: 10px;
   flex-wrap: wrap;

   .detail-field {
      flex: 1;
      min-width: 220px;
      display: flex;
      flex-direction: column;
      gap: 4px;
   }
   .detail-field label {
      font-size: 11px;
      font-weight: 700;
      text-transform: uppercase;
      letter-spacing: 0.03em;
      color: ${muted};
   }
`;
